import { useCallback, useState, useRef } from 'react';
import { AUDIO_FORMATS, MAX_AUDIO_SIZE } from '@recruitai/shared';

import { toast } from 'sonner';

interface AudioUploadZoneProps {
  onFileSelected: (file: File) => void;
  isUploading: boolean;
  progress: number;
}

export function AudioUploadZone({ onFileSelected, isUploading, progress }: AudioUploadZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSelect = useCallback((file: File) => {
    if (!AUDIO_FORMATS[file.type]) {
      toast.error('Formato não suportado. Use MP3, WAV, M4A ou OGG.');
      return;
    }
    if (file.size > MAX_AUDIO_SIZE) {
      toast.error('Arquivo excede o limite de 500MB.');
      return;
    }
    onFileSelected(file);
  }, [onFileSelected]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSelect(file);
  }, [validateAndSelect]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    e.target.value = '';
  }, [validateAndSelect]);

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
        } ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,.wav,.m4a,.ogg,audio/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <div className="text-4xl mb-2">🎙️</div>
        <p className="text-sm font-medium">Arraste o áudio aqui ou clique para selecionar</p>
        <p className="text-xs text-muted-foreground mt-1">MP3, WAV, M4A, OGG • Máx. 500MB</p>
      </div>

      {isUploading && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Enviando... {progress}%</p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}
